import { RuntimeEnvironmentGSEnum, RuntimeGS, RuntimeScopeGS } from "./RuntimeGS";

export type ConstructorGS<T> = new (...args: any[]) => T;
export type InjectionTokenGS<T = unknown> = ConstructorGS<T> | string | symbol;

/** Common application root with one-time lifecycle and lightweight DI. */
export abstract class App {
    private readonly _instances = new Map<InjectionTokenGS, unknown>();
    private readonly _factories = new Map<Function, (...args: any[]) => unknown>();
    private _startPromise?: Promise<void>;

    public buildId = "";
    public appRootSelector = "[gs-app]";

    /** @internal */
    protected abstract readonly environment: RuntimeEnvironmentGSEnum;

    /** Synchronous one-time application configuration phase. */
    protected initializeCore(): void {
    }

    protected abstract startCore(): Promise<void>;

    /** Current application in the browser or request scope. */
    public static get Inst(): App {
        const app = RuntimeGS.current?.app;
        if (!app) throw new Error("App.Inst is unavailable outside an application scope.");
        if (!(app instanceof this))
            throw new Error(`The current application is not an instance of ${this.name || "App"}.`);
        return app;
    }

    public start(): Promise<void> {
        if (!this._startPromise) this._startPromise = this.startInternal();
        return this._startPromise;
    }

    private async startInternal(): Promise<void> {
        const scope = this.createScope();
        await RuntimeGS.run(scope, async () => {
            scope.stateConfigurationOpen = true;
            try {
                this.initializeCore();
            } finally {
                scope.stateConfigurationOpen = false;
            }
            await this.startCore();
        });
    }

    /** @internal */
    protected createScope(): RuntimeScopeGS {
        return {
            app: this,
            environment: this.environment,
            instances: new Map<Function, unknown>()
        };
    }

    public addInst<T>(token: InjectionTokenGS<T>, instance: T): void {
        this._instances.set(token, instance);
    }

    public getInst<T>(token: InjectionTokenGS<T>): T {
        const scope = RuntimeGS.current;
        if (scope?.app === this && scope.instances.has(token as Function))
            return scope.instances.get(token as Function) as T;

        if (!this._instances.has(token))
            throw new Error(`No instance found for ${App.tokenName(token)}.`);
        return this._instances.get(token) as T;
    }

    public tryGetInst<T>(token: InjectionTokenGS<T>): T | undefined {
        const scope = RuntimeGS.current;
        if (scope?.app === this && scope.instances.has(token as Function))
            return scope.instances.get(token as Function) as T;
        return this._instances.get(token) as T | undefined;
    }

    public newInst<T>(constructor: ConstructorGS<T>, ...args: any[]): T {
        const factory = this._factories.get(constructor);
        return factory ? factory(...args) as T : new constructor(...args);
    }

    public regFactory<T>(constructor: ConstructorGS<T>, factory: (...args: any[]) => T): void {
        this._factories.set(constructor, factory);
    }

    private static tokenName(token: InjectionTokenGS): string {
        if (typeof token === "function") return token.name || "anonymous constructor";
        return String(token);
    }
}
